/** Report hub tab keys — must match backend ReportKey enum keys. */
import i18n from '../i18n';

export const REPORT_TABS_BY_ROLE = {
  ADMIN: [
    { key: "coach-revenue", enabledField: "coachRevenueEnabled" },
    { key: "session-statement", enabledField: "sessionStatementEnabled" },
    { key: "batch-attendance", enabledField: "batchAttendanceEnabled" },
    { key: "academy-revenue", enabledField: "academyRevenueEnabled" },
    { key: "student-attendance", enabledField: "studentAttendanceEnabled" },
    { key: "payments-due", enabledField: "paymentsDueEnabled" },
    { key: "data-export", enabledField: "dataExportEnabled" },
    { key: "sessions-summary", enabledField: "sessionsSummaryEnabled" },
    { key: "monthly-statements", enabledField: "monthlyStatementsEnabled" },
  ],
  MANAGER: [
    { key: "session-statement", enabledField: "sessionStatementEnabled" },
    { key: "batch-attendance", enabledField: "batchAttendanceEnabled" },
    { key: "student-attendance", enabledField: "studentAttendanceEnabled" },
  ],
  COACH: [
    { key: "batch-attendance", enabledField: "batchAttendanceEnabled" },
    { key: "student-attendance", enabledField: "studentAttendanceEnabled" },
  ],
};

export const ADMIN_REPORT_TOGGLES = [
  { key: "coach-revenue", enabledField: "coachRevenueEnabled", roles: ["ADMIN"] },
  { key: "session-statement", enabledField: "sessionStatementEnabled", roles: ["ADMIN", "MANAGER", "STUDENT", "PARENT"] },
  { key: "batch-attendance", enabledField: "batchAttendanceEnabled", roles: ["ADMIN", "MANAGER", "COACH"] },
  { key: "academy-revenue", enabledField: "academyRevenueEnabled", roles: ["ADMIN"] },
  { key: "student-attendance", enabledField: "studentAttendanceEnabled", roles: ["ADMIN", "MANAGER", "COACH"] },
  { key: "payments-due", enabledField: "paymentsDueEnabled", roles: ["ADMIN"] },
  { key: "data-export", enabledField: "dataExportEnabled", roles: ["ADMIN"] },
  { key: "sessions-summary", enabledField: "sessionsSummaryEnabled", roles: ["ADMIN"] },
  { key: "monthly-statements", enabledField: "monthlyStatementsEnabled", roles: ["ADMIN"] },
];

export function getReportTabs(t = i18n.t.bind(i18n)) {
  const localize = (tab, role) => ({
    ...tab,
    label: role === 'MANAGER' && tab.key === 'session-statement'
      ? t('reports.tabsManager.session-statement')
      : t(`reports.tabs.${tab.key}`),
  });

  return Object.fromEntries(
    Object.entries(REPORT_TABS_BY_ROLE).map(([role, tabs]) => [
      role,
      tabs.map((tab) => localize(tab, role)),
    ])
  );
}

export function getAdminReportToggles(t = i18n.t.bind(i18n)) {
  return ADMIN_REPORT_TOGGLES.map((report) => ({
    ...report,
    label: t(`reports.tabs.${report.key}`),
    description: t(`reports.descriptions.${report.key}`),
  }));
}

export function normalizeReportSettings(api = {}) {
  return {
    coachRevenueEnabled: api.coachRevenueEnabled !== false,
    sessionStatementEnabled: api.sessionStatementEnabled !== false,
    batchAttendanceEnabled: api.batchAttendanceEnabled !== false,
    academyRevenueEnabled: api.academyRevenueEnabled !== false,
    studentAttendanceEnabled: api.studentAttendanceEnabled !== false,
    dataExportEnabled: api.dataExportEnabled !== false,
    paymentsDueEnabled: api.paymentsDueEnabled !== false,
    sessionsSummaryEnabled: api.sessionsSummaryEnabled !== false,
    monthlyStatementsEnabled: api.monthlyStatementsEnabled !== false,
    coachBatchAttendanceShowCharges: api.coachBatchAttendanceShowCharges === true,
    batchAcademyCommissionPercent: api.batchAcademyCommissionPercent ?? 30,
    individualAcademyCommissionPercent:
      api.individualAcademyCommissionPercent ?? 20,
    enabledReports: Array.isArray(api.enabledReports) ? api.enabledReports : [],
  };
}

export function isReportEnabled(settings, key) {
  if (!settings) return true;
  // Prefer explicit per-report toggles (covers reports whose keys are not yet
  // in the backend enabledReports list).
  const tab = ADMIN_REPORT_TOGGLES.find((r) => r.key === key);
  if (tab) {
    return settings[tab.enabledField] !== false;
  }
  if (
    Array.isArray(settings.enabledReports) &&
    settings.enabledReports.length > 0
  ) {
    return settings.enabledReports.includes(key);
  }
  return true;
}

export function tabsForRole(role, settings, t = i18n.t.bind(i18n)) {
  const roleTabs = REPORT_TABS_BY_ROLE[role] || REPORT_TABS_BY_ROLE.ADMIN;
  return roleTabs
    .filter((tab) => isReportEnabled(settings, tab.key))
    .map((tab) => ({
      ...tab,
      label: role === 'MANAGER' && tab.key === 'session-statement'
        ? t('reports.tabsManager.session-statement')
        : t(`reports.tabs.${tab.key}`),
    }));
}
